import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
  type RefObject,
} from "react";

import { YoutubePlayerControls } from "./YoutubePlayerControls";

import { youtubeEmbedHostCss, youtubePointerShieldCss } from "./youtubeEmbed";

const CHROME_HIDE_DELAY_MS = 2000;

export type YoutubePlayerChromeProps = {
  children: ReactNode;

  className?: string;

  style?: CSSProperties;

  isPlaying: boolean;

  currentTime: number;

  duration: number;

  onTogglePlay: () => void;

  onSeek: (seconds: number) => void;

  /** Element to fullscreen; defaults to the chrome root. */

  fullscreenTargetRef?: RefObject<HTMLElement | null>;

  showFullscreen?: boolean;

  hideControls?: boolean;
};

const rootStyle: CSSProperties = {
  position: "relative",

  width: "100%",

  height: "100%",
};

export function YoutubePlayerChrome({
  children,

  className,

  style,

  isPlaying,

  currentTime,

  duration,

  onTogglePlay,

  onSeek,

  fullscreenTargetRef,

  showFullscreen = true,

  hideControls = false,
}: YoutubePlayerChromeProps) {
  const rootRef = useRef<HTMLDivElement | null>(null);

  const [isFullscreen, setIsFullscreen] = useState(false);

  const [chromeVisible, setChromeVisible] = useState(false);

  const [scrubbing, setScrubbing] = useState(false);

  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearHideTimer = useCallback(() => {
    if (hideTimerRef.current != null) {
      clearTimeout(hideTimerRef.current);

      hideTimerRef.current = null;
    }
  }, []);

  const scheduleHide = useCallback(() => {
    clearHideTimer();

    if (!isPlaying || scrubbing) return;

    hideTimerRef.current = setTimeout(() => {
      setChromeVisible(false);

      hideTimerRef.current = null;
    }, CHROME_HIDE_DELAY_MS);
  }, [clearHideTimer, isPlaying, scrubbing]);

  const revealChrome = useCallback(() => {
    if (hideControls) return;

    setChromeVisible(true);

    scheduleHide();
  }, [hideControls, scheduleHide]);

  const hideChromeNow = useCallback(() => {
    clearHideTimer();

    if (!isPlaying || scrubbing) return;

    setChromeVisible(false);
  }, [clearHideTimer, isPlaying, scrubbing]);

  useEffect(() => {
    if (!isPlaying || scrubbing) {
      setChromeVisible(true);

      clearHideTimer();

      return;
    }

    if (!chromeVisible) return;

    scheduleHide();

    return clearHideTimer;
  }, [isPlaying, scrubbing, chromeVisible, scheduleHide, clearHideTimer]);

  useEffect(() => () => clearHideTimer(), [clearHideTimer]);

  useEffect(() => {
    const onChange = () => {
      setIsFullscreen(Boolean(document.fullscreenElement));
    };

    document.addEventListener("fullscreenchange", onChange);

    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);

  const toggleFullscreen = useCallback(() => {
    const el = fullscreenTargetRef?.current ?? rootRef.current;

    if (!el) return;

    if (document.fullscreenElement) {
      void document.exitFullscreen?.();
    } else {
      void el.requestFullscreen?.();
    }
  }, [fullscreenTargetRef]);

  const onShieldClick = useCallback(() => {
    onTogglePlay();

    revealChrome();
  }, [onTogglePlay, revealChrome]);

  const showControlsOverlay =
    !hideControls && (chromeVisible || !isPlaying || scrubbing);

  return (
    <div
      ref={rootRef}
      className={className}
      style={{ ...rootStyle, ...style }}
      data-youtube-player-chrome
      onMouseEnter={revealChrome}
      onMouseMove={revealChrome}
      onMouseLeave={hideChromeNow}
    >
      <div style={youtubeEmbedHostCss}>{children}</div>

      {!hideControls ? (
        <div
          aria-hidden
          style={youtubePointerShieldCss}
          onClick={onShieldClick}
          onTouchStart={revealChrome}
          onDoubleClick={(e) => {
            e.preventDefault();

            toggleFullscreen();
          }}
        />
      ) : null}

      <YoutubePlayerControls
        isPlaying={isPlaying}
        currentTime={currentTime}
        duration={duration}
        onTogglePlay={onTogglePlay}
        onSeek={onSeek}
        onToggleFullscreen={toggleFullscreen}
        isFullscreen={isFullscreen}
        showFullscreen={showFullscreen}
        hideControls={hideControls}
        visible={showControlsOverlay}
        onReveal={revealChrome}
        onScrubbingChange={setScrubbing}
      />
    </div>
  );
}
